import { Component, effect, input, OnInit, output, signal } from '@angular/core';
import { Client, ServiceProviderClient } from '../../models/client';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ServiceProviderService } from '../service-provider.service';
import { NotificationService } from '../../shared/notification.service';
import { AppModule } from '../../app.module';
import { MapSelect } from "../../map/map-select/map-select";
import { IncidentLocation } from '../../models/incident';
import { finalize } from 'rxjs';
import { WhatsappService } from '../../whatsapp/whatsapp.service';

@Component({
  selector: 'app-service-provider-edit',
  imports: [AppModule, MapSelect],
  templateUrl: './service-provider-edit.html',
  styleUrl: './service-provider-edit.less'
})
export class ServiceProviderEdit  {

  isVisible = input.required<boolean>();
  provider = input.required<ServiceProviderClient | null>();

  visibleChange = output<boolean>();
  providerSaved = output<ServiceProviderClient>();

  isLoading = signal<boolean>(false);
  isLoadingGroupName = signal<boolean>(false);
  form = new FormGroup({
    clientId: new FormControl(0),
    clientName: new FormControl('', [Validators.required, Validators.minLength(3)]),
    clientTel: new FormControl('', [Validators.required, Validators.pattern(/^[\d\s\-\+\(\)]+$/)]),
    latitude: new FormControl(0, [Validators.required, Validators.min(-90), Validators.max(90)]),
    longitude: new FormControl(0, [Validators.required, Validators.min(-180), Validators.max(180)]),
    addressCity: new FormControl('', [Validators.required, Validators.pattern(/\S/)]),
    pricingName: new FormControl(''),
    pricingMethod: new FormControl(0),
    pricingValue: new FormControl(0),
    clientCell: new FormControl('', [Validators.pattern(/^[\d\s\-\+\(\)]+$/)]),
    whatsappGroupLink: new FormControl('', [Validators.pattern(/^(https?:\/\/)?(www\.)?chat\.whatsapp\.com\/.+$/)]),
    whatsappGroupName: new FormControl('')
  })

  mapLocation = signal<IncidentLocation | undefined>(undefined);
  constructor(
    private spService: ServiceProviderService,
    private notification: NotificationService,
    private whatsappService: WhatsappService,
  ) { 

    effect(() => {
      if (this.isVisible()) {
        this.populateForm();
      }
    })
   }

  // ngOnInit() {
  //     this.populateForm();
  // }

  private populateForm() {

    const provider = this.provider();
    if (provider != null) {
      this.form.patchValue({
        clientName: provider.clientName,
        clientTel: provider.clientTel,
        latitude: provider.latitude,
        longitude: provider.longitude,
        addressCity: provider.addressCity,
        pricingName: provider.pricingTemplate?.pricingName || '',
        pricingMethod: provider.pricingTemplate?.pricingMethod || 0,
        pricingValue: provider.pricingTemplate?.pricingValue  || 0,
        clientCell: provider.clientCell || '',
        whatsappGroupLink: provider.whatsappGroupLink || '',
        whatsappGroupName: provider.whatsappGroupName || ''
      });

      this.mapLocation.set({
        latitude: provider.latitude,
        longitude: provider.longitude,
        description: provider.clientName,
        descriptionShort: provider.clientName
      });

    }
  else {
      this.form.reset();
      this.mapLocation.set(undefined);
    }
  }

  public onLocationSelected(location: IncidentLocation | null) {
    if (location) {
      this.form.patchValue({
        latitude: location.latitude,
        longitude: location.longitude
      });
      this.mapLocation.set(location);
    }
  }

  public onGroupLinkBlur(): void {
    const link = this.form.get('whatsappGroupLink')?.value;
    if (!link) { return; }
    this.isLoadingGroupName.set(true);
    this.form.get('whatsappGroupName')?.setValue('');
    this.form.get('whatsappGroupName')?.disable();
    this.whatsappService.getGroupName(link).subscribe({
      next: (result) => {
        this.form.get('whatsappGroupName')?.setValue(result.groupName || '');
        this.form.get('whatsappGroupName')?.enable();
        this.isLoadingGroupName.set(false);
      },
      error: () => {
        this.form.get('whatsappGroupName')?.enable();
        this.isLoadingGroupName.set(false);
      }
    });
  }

  public handleCancel() {
    this.form.reset();
    this.visibleChange.emit(false);
  }

  public handleSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.notification.showError('Please correct the errors in the form before saving.');
      return;
    }

    this.isLoading.set(true);
    try {
      const existingProvider = this.provider();
      const rawValues = this.form.getRawValue();

      const updatedProvider: ServiceProviderClient = {
        clientId: existingProvider?.clientId || 0,  // 0 for new provider
        connectionTypeId: existingProvider?.connectionTypeId || 3,  // connection type for SPs
        clientName: rawValues.clientName || '',
        clientTel: rawValues.clientTel || '',
        latitude: rawValues.latitude || 0,
        longitude: rawValues.longitude || 0,
        addressCity: rawValues.addressCity?.trim() || null,
        dtLastModified: new Date(),
        pricingTemplate: {
          pricingTemplateId: existingProvider?.pricingTemplate?.pricingTemplateId || 0,
          clientId: existingProvider?.clientId || 0,
          description: existingProvider?.pricingTemplate?.description || '',
          pricingName: rawValues.pricingName || '',
          pricingMethod: rawValues.pricingMethod || 0,
          pricingValue: rawValues.pricingValue || 0,
        },
        whatsappGroupLink: rawValues.whatsappGroupLink || '',
        whatsappGroupName: rawValues.whatsappGroupName || '',
        technicians: existingProvider?.technicians || [],
        breakdownsAllocated: existingProvider?.breakdownsAllocated || 0,
        clientGroupId: existingProvider?.clientGroupId,
        clientGroupName: existingProvider?.clientGroupName,
        clientGroupClassificationId: existingProvider?.clientGroupClassificationId,
        clientGroupClassification: existingProvider?.clientGroupClassification,
        clientCell: rawValues.clientCell || null
      };

      this.spService.updateServiceProvider(updatedProvider)
        .pipe(
          finalize(() => this.isLoading.set(false))
        )
        .subscribe({
          next: (savedProvider: ServiceProviderClient) => {
            this.notification.showSuccess('Service provider updated successfully.');
            this.providerSaved.emit(savedProvider);
            this.visibleChange.emit(false);
          },
          error: (error) => {
            this.notification.handleError(error, 'Error saving service provider');
          }
        });
    } catch (ex) {
      this.isLoading.set(false);
      console.error(ex);
      this.notification.showError('An unexpected error occurred while preparing data.');
    }
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  public getFieldErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field || !field.errors) return '';
    
    if (fieldName === 'addressCity' && (field.errors['required'] || field.errors['pattern'])) return 'Town/City is required';
    if (field.errors['required']) return 'This field is required';
    if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters required`;
    if (field.errors['pattern']) return 'Invalid format';
    if (field.errors['min']) return `Minimum value is ${field.errors['min'].min}`;
    if (field.errors['max']) return `Maximum value is ${field.errors['max'].max}`;
    
    return 'Invalid value';
  }

}
